import React, { useEffect, useId, useRef, useState } from "react";
import { CloudArrowUp, X } from "@phosphor-icons/react";
import "./PrescriptionUpload.css";

const isImage = (file) => file.type
  ? file.type.startsWith("image/")
  : /\.(jpe?g|png|webp|gif|bmp|avif|heic|heif|svg|tiff?)$/i.test(file.name);

export default function PrescriptionUpload({ file, onChange, disabled = false }) {
  const inputRef = useRef(null);
  const dragDepth = useRef(0);
  const titleId = useId();
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (!file || typeof URL.createObjectURL !== "function") { setPreview(""); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const selectFile = (selected) => {
    if (disabled || !selected) return;
    if (!isImage(selected)) { setError("Please select an image file."); return; }
    setError("");
    onChange(selected);
  };

  return (
    <section className={`prescription-upload${dragging ? " is-dragging" : ""}`} aria-labelledby={titleId}
      onDragEnter={(event) => {
        event.preventDefault(); event.stopPropagation();
        if (!disabled) { dragDepth.current += 1; setDragging(true); }
      }}
      onDragLeave={(event) => {
        event.preventDefault(); event.stopPropagation();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (!dragDepth.current) setDragging(false);
      }}
      onDragOver={(event) => {
        event.preventDefault(); event.stopPropagation();
        if (event.dataTransfer) event.dataTransfer.dropEffect = disabled ? "none" : "copy";
      }}
      onDrop={(event) => {
        event.preventDefault(); event.stopPropagation();
        dragDepth.current = 0; setDragging(false);
        selectFile(event.dataTransfer?.files?.[0]);
      }}>
      <h3 id={titleId}>Upload Prescription</h3>
      <input ref={inputRef} type="file" name="prescription_image" accept="image/*" aria-label="Prescription image"
        hidden disabled={disabled} onChange={(event) => { selectFile(event.target.files?.[0]); event.target.value = ""; }} />
      {file ? (
        <div className="prescription-upload__selected">
          {preview && <img src={preview} alt="Selected prescription" />}
          <button type="button" className="prescription-upload__filename" disabled={disabled}
            onClick={() => inputRef.current?.click()} aria-label="Replace prescription image" title={file.name}>{file.name}</button>
          <button type="button" className="prescription-upload__remove" aria-label="Remove prescription image"
            disabled={disabled} onClick={() => { setError(""); onChange(null); }}><X size={18} aria-hidden="true" /></button>
        </div>
      ) : (
        <button type="button" className="prescription-upload__browse" aria-label="Upload prescription image" disabled={disabled}
          onClick={() => inputRef.current?.click()}>
          <CloudArrowUp size={28} weight="regular" aria-hidden="true" />
          <span>Drop your image, or <span className="prescription-upload__link">browse</span></span>
        </button>
      )}
      {error && <p className="prescription-upload__error" role="alert">{error}</p>}
    </section>
  );
}
